import { useEffect } from 'react';

const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
const API_URL = (process.env.REACT_APP_API_URL || `${window.location.origin}/api`).replace(/\/api\/?$/, '');
const MANAGED_ATTRIBUTE = 'data-jms-seo';

const upsertMeta = (attributes) => {
  const element = document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  element.setAttribute(MANAGED_ATTRIBUTE, 'true');
  document.head.appendChild(element);
};

const Seo = ({
  title,
  description,
  canonicalPath,
  type = 'website',
  image,
  jsonLd,
  citation,
  noIndex = false,
}) => {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd).replace(/</g, '\\u003c') : '';
  const citationString = citation ? JSON.stringify(citation) : '';

  useEffect(() => {
    const canonicalUrl = canonicalPath?.startsWith('http') ? canonicalPath : `${SITE_URL}${canonicalPath || window.location.pathname}`;
    const absoluteImage = image ? (image.startsWith('http') ? image : `${SITE_URL}${image}`) : null;
    const previousTitle = document.title;
    document.querySelectorAll(`[${MANAGED_ATTRIBUTE}]`).forEach(node => node.remove());
    document.querySelectorAll('meta[name="description"], link[rel="canonical"]').forEach(node => node.remove());
    document.title = title;

    upsertMeta({ name: 'description', content: description });
    upsertMeta({ property: 'og:site_name', content: 'NSUK Journal Management System' });
    upsertMeta({ property: 'og:type', content: type });
    upsertMeta({ property: 'og:title', content: title });
    upsertMeta({ property: 'og:description', content: description });
    upsertMeta({ property: 'og:url', content: canonicalUrl });
    upsertMeta({ name: 'twitter:card', content: absoluteImage ? 'summary_large_image' : 'summary' });
    upsertMeta({ name: 'twitter:title', content: title });
    upsertMeta({ name: 'twitter:description', content: description });
    if (absoluteImage) {
      upsertMeta({ property: 'og:image', content: absoluteImage });
      upsertMeta({ name: 'twitter:image', content: absoluteImage });
    }
    if (noIndex) upsertMeta({ name: 'robots', content: 'noindex, nofollow' });

    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = canonicalUrl;
    canonical.setAttribute(MANAGED_ATTRIBUTE, 'true');
    document.head.appendChild(canonical);

    if (citationString) {
      const citationData = JSON.parse(citationString);
      if (citationData.title) upsertMeta({ name: 'citation_title', content: citationData.title });
      (citationData.authors || []).forEach(author => upsertMeta({ name: 'citation_author', content: author }));
      const fields = {
        citation_publication_date: citationData.publicationDate,
        citation_journal_title: citationData.journalTitle,
        citation_volume: citationData.volume,
        citation_issue: citationData.issue,
        citation_firstpage: citationData.firstPage,
        citation_lastpage: citationData.lastPage,
        citation_doi: citationData.doi,
        citation_pdf_url: citationData.pdfUrl,
        citation_keywords: citationData.keywords,
      };
      Object.entries(fields).forEach(([name, content]) => content && upsertMeta({ name, content: String(content) }));
      const publicationId = canonicalUrl.split('/').filter(Boolean).pop();
      const alternates = [
        ['application/vnd.citationstyles.csl+json', 'csl'],
        ['application/x-bibtex', 'bibtex'],
        ['application/x-research-info-systems', 'ris'],
        ['application/xml', 'jats'],
      ];
      alternates.forEach(([alternateType, format]) => {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.type = alternateType;
        link.href = `${API_URL}/api/seo/publications/${publicationId}/citation.${format}`;
        link.setAttribute(MANAGED_ATTRIBUTE, 'true');
        document.head.appendChild(link);
      });
    }

    if (jsonLdString) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = jsonLdString;
      script.setAttribute(MANAGED_ATTRIBUTE, 'true');
      document.head.appendChild(script);
    }

    return () => {
      document.querySelectorAll(`[${MANAGED_ATTRIBUTE}]`).forEach(node => node.remove());
      document.title = previousTitle;
    };
  }, [title, description, canonicalPath, type, image, jsonLdString, citationString, noIndex]);

  return null;
};

export default Seo;
